import { useEffect } from "react";
import { Box, Chip, Switch, TextField, Typography, Stack } from "@mui/material";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import type { AlertChannel } from "../services/inventoryApi";

export type LowStockValue = {
  enabled: boolean;
  threshold: string;
  alertOnce: boolean;
  cooldownHours: string;
  channels: AlertChannel[];
};
const PUSH: AlertChannel = "push";
const ensurePush = (arr: AlertChannel[]) =>
  arr.includes(PUSH) ? arr : [PUSH, ...arr];
const removeChannel = (arr: AlertChannel[], ch: AlertChannel) =>
  arr.filter((c) => c !== ch);

export default function LowStockControlsWeb({
  value,
  onChange,
  title = "Low Stock Alert",
}: {
  value: LowStockValue;
  onChange: (v: LowStockValue) => void;
  title?: string;
}) {
  // Keep 'push' forced when enabled (same behavior as mobile)
  useEffect(() => {
    if (value.enabled && !value.channels.includes(PUSH)) {
      onChange({ ...value, channels: ensurePush(value.channels) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.enabled]);

  const toggleChannel = (ch: AlertChannel) => {
    if (ch === PUSH) {
      // Only toggle push when disabled
      if (!value.enabled) {
        const exists = value.channels.includes(PUSH);
        const channels = exists
          ? removeChannel(value.channels, PUSH)
          : [PUSH, ...value.channels];
        onChange({ ...value, channels });
      }
      return;
    }

    const exists = value.channels.includes(ch);
    let channels = exists
      ? removeChannel(value.channels, ch)
      : [...value.channels, ch];

    if (value.enabled) channels = ensurePush(channels);
    onChange({ ...value, channels });
  };

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        p: 2,
        mt: 1.5,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} mb={1}>
        <NotificationsActiveIcon fontSize="small" />
        <Typography variant="body1">{title}</Typography>
        <Box sx={{ flex: 1 }} />
        <Switch
          checked={value.enabled}
          onChange={(_, enabled) => {
            onChange({
              ...value,
              enabled,
              channels: enabled ? ensurePush(value.channels) : value.channels,
            });
          }}
        />
      </Stack>

      <Box sx={{ opacity: value.enabled ? 1 : 0.6 }}>
        <Stack direction="row" spacing={2} mb={1.5}>
          <TextField
            label="Threshold (qty)"
            size="small"
            type="text"
            inputMode="decimal"
            value={value.threshold}
            onChange={(e) => {
              const t = e.target.value;
              if (t === "" || /^[0-9]*\.?[0-9]*$/.test(t)) {
                onChange({ ...value, threshold: t });
              }
            }}
            disabled={!value.enabled}
          />
          <TextField
            label="Cooldown (hrs)"
            size="small"
            type="text"
            inputMode="numeric"
            value={value.cooldownHours}
            onChange={(e) => {
              const t = e.target.value;
              if (t === "" || /^[0-9]*$/.test(t)) {
                onChange({ ...value, cooldownHours: t });
              }
            }}
            disabled={!value.enabled}
          />
        </Stack>

        <Stack direction="row" alignItems="center" spacing={1} mb={1}>
          <Chip
            label="Alert once (until stock recovers)"
            onClick={() =>
              value.enabled &&
              onChange({ ...value, alertOnce: !value.alertOnce })
            }
            icon={
              value.alertOnce ? (
                <CheckBoxIcon fontSize="small" />
              ) : (
                <CheckBoxOutlineBlankIcon fontSize="small" />
              )
            }
            variant="outlined"
            color={value.alertOnce ? "primary" : "default"}
            disabled={!value.enabled}
          />
        </Stack>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mb: 0.5, display: "block" }}
        >
          Channel
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          {(["push"] as const).map((ch) => {
            const selected = value.channels.includes(ch);
            const disabled = !value.enabled;
            return (
              <Chip
                key={ch}
                label={ch}
                variant={selected ? "filled" : "outlined"}
                color={selected ? "primary" : "default"}
                onClick={() => toggleChannel(ch)}
                disabled={disabled}
              />
            );
          })}
        </Stack>
      </Box>
    </Box>
  );
}
